<template>
  <div
    class="flex flex-col gap-2 rounded border bg-primary-100 p-2 select-none"
  >
    <div>
      <div class="flex flex-row items-center">
        <div
          class="flex flex-1 flex-row items-center gap-2 truncate text-lg font-bold"
        >
          <FontAwesomeIcon v-if="icon" :icon="['fal', icon]" size="lg" />
          <span>
            {{ $h.formatCurrency(payment.amount, invoice?.currency) }}
          </span>
          <img
            v-if="payment.chift_id"
            :src="chiftLogo"
            :title="$t('invoicing.payment_sync_causer')"
            class="h-4"
            alt="chift"
          />
        </div>
        <div class="flex shrink-0 gap-x-1">
          <div>
            <p
              v-if="payment.is_payment_deposit"
              class="rounded-md bg-theme-success px-1 py-0.5 text-xs font-semibold text-black"
            >
              {{ $t('attributes.deposit') }}
            </p>
          </div>
          <div>
            <p
              v-if="paymentInAwating"
              class="rounded-md bg-orange-200 px-1 py-0.5 text-xs font-semibold text-black"
            >
              {{ $t('attributes.snoozed') }}
            </p>
          </div>
          <template v-if="!notLinkedPayment">
            <div>
              <BaseButton
                v-if="!paymentInAwating && $can('see_payment')"
                icon="print"
                color="primary"
                size="extra-small"
                :title="$t('global.payment_proof')"
                @click="printPayment"
              />
            </div>
            <div>
              <BaseButton
                v-if="payment.tier_payant && $can('see_payment')"
                icon="file-invoice"
                color="primary"
                size="extra-small"
                :title="$t('global.third_payment')"
                @click="printThirdPartyInvoice"
              />
            </div>
            <div>
              <BaseButton
                icon="pen"
                color="primary"
                size="extra-small"
                :disabled="!$can('manage_payment')"
                @click="isPaymentFormModalOpen = true"
              />
            </div>
            <div>
              <BaseButton
                v-if="deleteChoices.length"
                :icon="canDeletePayment ? 'trash' : 'unlink'"
                :icon-type="canDeletePayment ? 'fal' : 'fas'"
                :color="canDeletePayment ? 'danger' : 'primary'"
                size="extra-small"
                @click.stop="deletePayment"
              />
            </div>
          </template>
          <template v-else>
            <div>
              <BaseButton
                icon="link"
                icon-type="fas"
                color="primary"
                size="extra-small"
                :disabled="!$can('link_payments_from_accounting')"
                :title="$t('button.payment.link_payment')"
                @click="$emit('link-payment', payment.id)"
              />
            </div>
          </template>
        </div>
      </div>
      <div class="flex flex-wrap gap-2">
        <span
          v-for="invoice in payment.invoices"
          :key="invoice.id"
          class="rounded bg-secondary px-1 text-xs text-contrast-secondary"
        >
          {{
            invoice.reference ??
            `${$t(`enums.invoicing.docTypes.${invoice.doc_type}`)}: ${$t('global.doc_type_to_be_validated')}`
          }}
        </span>
      </div>
    </div>
    <div>
      <ViewData :data="dataView" size="extra-small" />
    </div>
    <PaymentFormModal
      v-model:is-modal-open="isPaymentFormModalOpen"
      :payment-id="payment.id"
      :model="invoice"
      :place-holder-direct-debit="invoice.dossier?.concernedPerson?.full_name"
      model-type="invoice"
      dossier-contacts=""
      :place-holder-issuer="invoice.invoiceable?.full_name"
    />
    <ConfirmModal
      ref="1234"
      :disabled-ok-button="deleteChoices.length > 1 && !selectedDeleteChoice"
    >
      <template #default>
        <div class="mt-3 divide-y rounded-lg border">
          <label
            v-for="deleteChoice in deleteChoices"
            class="flex cursor-pointer flex-row gap-3 p-3"
            :class="{
              'text-red-500': deleteChoice.danger
            }"
          >
            <div class="flex shrink-0 items-center justify-center">
              <input
                v-model="selectedDeleteChoice"
                :value="deleteChoice.value"
                type="radio"
                name="delete_choice"
                class="text-primary focus:ring-0 focus:ring-offset-0 active:ring-0 active:ring-offset-0"
              />
            </div>
            <div class="flex-1">
              <h5 class="font-bold capitalize-first">
                {{ deleteChoice.title }}
              </h5>
              <p
                class="text-xs capitalize-first"
                v-html="deleteChoice.description"
              ></p>
            </div>
          </label>
        </div>
      </template>
    </ConfirmModal>
  </div>
</template>

<script>
import PaymentFormModal from '@ui/modal/payment/PaymentFormModal.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { mapGetters } from 'vuex'
import PaymentInvoiceFormModal from '@ui/modal/payment/PaymentInvoiceFormModal.vue'
import ViewData from '@c/ViewComponents/ViewData.vue'
import { issuingBankName } from '@ui/modal/payment/issuingBank'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import PartnersLogo from '@/assets/enums/config/PartnersLogo'

export default {
  name: 'PaymentModalCard',
  components: {
    ConfirmModal,
    ViewData,
    BaseButton,
    FontAwesomeIcon,
    PaymentFormModal
  },
  props: {
    payment: {
      type: Object,
      required: true
    },
    notLinkedPayment: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['delete', 'link-payment', 'payment-updated'],
  data() {
    return {
      isPaymentFormModalOpen: false,
      selectedDeleteChoice: null
    }
  },
  computed: {
    ...mapGetters({
      initialMetadata: 'metadata/getInitialMetadata',
      invoice: 'invoicing/getInvoice',
      payment_modes: 'metadata/getPaymentModesMetadata'
    }),
    canDeletePayment() {
      return this.$can('delete_payment')
    },
    deleteChoices() {
      return [
        // {
        //   value: 'cancel_payment',
        //   title: this.$t('global.payment.cancel_payment'),
        //   description: this.$t('global.payment.cancel_payment_desc'),
        //   danger: false
        // },
        this.$can('link_payments_from_accounting') && {
          value: 'unlink_payment',
          title: this.$t('global.payment.unlink_payment'),
          description: this.$t('global.payment.unlink_payment_desc'),
          danger: false
        },
        this.$can('delete_payment') && {
          value: 'delete_payment',
          title: this.$t('global.payment.delete_payment'),
          description: this.$t('global.payment.delete_payment_desc'),
          danger: true
        }
      ].filter(Boolean)
    },
    issuingBank() {
      return issuingBankName(this.payment, this.initialMetadata?.models?.banks)
    },
    dataView() {
      return [
        [
          [
            {
              label: this.$t('attributes.emission_date'),
              value: this.$h.formatDate(this.payment.emission_date, 'LL'),
              gate: this.payment.payment_status === 'REC'
            },
            {
              label: this.$t('global.third_payment'),
              value:
                this.payment.payer?.full_name ?? this.payment.issuer_name ?? '',
              gate: !!this.payment.tier_payant
            },
            {
              label: this.$t('attributes.issuer_name'),
              value:
                this.payment.payment_mode === 'LEV'
                  ? this.$t('enums.invoicing.paymentMode.LEV')
                  : (this.payment.payer?.full_name ??
                    this.payment.issuer_name ??
                    this.invoice?.invoiceable?.full_name),
              gate: !this.payment.tier_payant
            },
            {
              label: this.$t('attributes.check_issuer_bank'),
              value: this.issuingBank,
              gate: !!this.issuingBank
            },
            {
              label: this.$t('attributes.remarks'),
              value: this.payment.comment,
              gate: !!this.payment.comment
            }
          ]
        ],
        []
      ]
    },
    icon() {
      return (
        this.payment_modes.find(
          (payment_mode) => payment_mode.id === this.payment.payment_mode
        )?.icon ?? null
      )
    },
    chiftLogo() {
      return PartnersLogo.chift
    },
    paymentInAwating() {
      if (this.payment.payment_status === 'AWA') {
        return true
      }
      const chequeDeductionRule =
        this.initialMetadata?.settings?.default_cheque_deduction
      if (chequeDeductionRule === 'MCA') {
        if (
          this.payment.payment_mode === 'CHE' &&
          this.payment.check_status !== 'CAS'
        ) {
          return true
        }
      }
      if (chequeDeductionRule === 'CID') {
        if (this.payment.payment_mode === 'CHE') {
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          const expectingCashingDate = new Date(
            this.payment.expected_cashing_date
          )
          expectingCashingDate.setHours(0, 0, 0, 0)
          if (expectingCashingDate > today) {
            return true
          }
        }
      }
      return false
    }
  },
  watch: {
    isPaymentFormModalOpen(newValue, oldValue) {
      if (newValue !== oldValue && !newValue) {
        this.$emit('payment-updated')
      }
    }
  },
  methods: {
    async deletePayment() {
      if (this.deleteChoices.length === 1) {
        this.selectedDeleteChoice = this.deleteChoices[0].value
      }

      let result = await this.$refs['1234'].show({
        title: this.canDeletePayment
          ? this.$t('global.payment.delete_payment')
          : this.$t('global.payment.unlink_payment_title')
      })
      if (result) {
        this.$emit('delete', this.payment.id, this.selectedDeleteChoice)
      }
    },
    printPayment() {
      this.$router.push({
        name: 'invoice_print_payment_route',
        params: {
          invoicing_id: this.$route.params.invoicing_id,
          payment_id: this.payment.id
        }
      })
    },
    printThirdPartyInvoice() {
      this.$router.push({
        name: 'invoice_print_third_party_payment_route',
        params: {
          invoicing_id: this.$route.params.invoicing_id,
          payment_id: this.payment.id
        }
      })
    }
  }
}
</script>
